import React, { useMemo, useState } from "react";
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Badge, Box, Checkbox, Chip, CircularProgress, FormControl,
  IconButton, InputLabel, Link, MenuItem, Paper, Select, Snackbar, Stack, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AnalyticsPageShell, { CARD_SX } from "./AnalyticsPageShell";
import { TaskDialog, useRoadmapProgress } from "./RoadmapTodo";
import {
  LAST_CHECKED, OUR_SITE, SECTIONS, STATUS, flattenSeoFoundationActions, foundationSummary, seoFoundationTaskId,
} from "./marketingSeoOriginBacklinksData";

const PURPLE = "#7c3aed";
const DONE_GREEN = "#047857";

// ---------- pure helpers (exported for tests) ----------

export function filterSections(sections, { pillar = "all", status = "all", foundation = "all", search = "" }, states = {}) {
  const q = search.trim().toLowerCase();
  return sections
    .filter((s) => pillar === "all" || s.id === pillar)
    .map((s) => {
      const topics = s.topics
        .filter((t) => foundation === "all" || t.status === foundation)
        .filter((t) => !q || [s.label, t.name, t.leader.name, t.leader.note, t.us.note, t.gap, ...t.actions.map((x) => x.title)].join(" ").toLowerCase().includes(q))
        .map((t) => ({
          ...t,
          visibleActions: t.actions.filter((act) => {
            const done = !!states[seoFoundationTaskId(s.id, t.id, act.id)]?.completed;
            return status === "all" || (status === "open" ? !done : done);
          }),
        }))
        .filter((t) => status === "all" || t.visibleActions.length);
      return { ...s, topics };
    })
    .filter((s) => s.topics.length);
}

function formatYmd(ymd) {
  const d = new Date(`${ymd}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? ymd : new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(d);
}

function ActionRow({ task, state, disabled, busy, onToggle, onComments }) {
  const done = !!state?.completed;
  const count = state?.comment_count || 0;
  return (
    <Box data-testid={`seo-foundation-task-${task.id}`} sx={{ display: "flex", gap: 1, py: 0.75, borderTop: "1px dashed #eef0f4", "&:first-of-type": { borderTop: 0 } }}>
      <Checkbox
        size="small"
        checked={done}
        disabled={disabled || busy}
        onChange={(e) => onToggle(task, e.target.checked)}
        icon={<CheckBoxOutlineBlankIcon fontSize="small" sx={{ color: PURPLE }} />}
        checkedIcon={<CheckBoxIcon fontSize="small" sx={{ color: DONE_GREEN }} />}
        inputProps={{ "aria-label": `Mark done: ${task.title}` }}
        sx={{ p: 0.5, alignSelf: "flex-start" }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, textDecoration: done ? "line-through" : "none", color: done ? "text.secondary" : "text.primary" }}>
          {task.title}
          {task.effort && <Chip size="small" variant="outlined" label={`~${task.effort}`} sx={{ ml: 1, height: 20, fontSize: 11 }} />}
        </Typography>
        {task.detail && <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{task.detail}</Typography>}
        {done && (
          <Typography variant="caption" sx={{ display: "block", color: DONE_GREEN, fontWeight: 700 }} data-testid={`seo-foundation-done-${task.id}`}>
            Done{state.completed_by?.name ? ` by ${state.completed_by.name}` : ""}
          </Typography>
        )}
      </Box>
      <Tooltip title="Comments and files">
        <span>
          <IconButton size="small" onClick={() => onComments(task)} disabled={disabled} aria-label={`Comments (${count}): ${task.title}`}>
            <Badge badgeContent={count} color="secondary" max={99}><ChatBubbleOutlineIcon fontSize="small" /></Badge>
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
}

function StatChip({ label, value, testId, color = PURPLE }) {
  return <Chip data-testid={testId} label={`${label}: ${value}`} sx={{ fontWeight: 700, bgcolor: `${color}14`, color }} />;
}

export default function MarketingSeoOriginBacklinks() {
  const progress = useRoadmapProgress();
  const [filters, setFilters] = useState({ pillar: "all", status: "all", foundation: "all", search: "" });
  const [dialog, setDialog] = useState({ task: null, mode: "comments" });
  const [busyKey, setBusyKey] = useState(null);
  const [toast, setToast] = useState(null);

  const allActions = useMemo(() => flattenSeoFoundationActions(), []);
  const summary = useMemo(() => foundationSummary(), []);
  const disabled = progress.loading || !!progress.error;
  const openCount = allActions.filter((t) => !progress.tasks[t.id]?.completed).length;
  const shown = filterSections(SECTIONS, filters, progress.tasks);
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const onToggle = async (task, completed) => {
    setBusyKey(task.id);
    try {
      await progress.toggle(task.id, completed);
      if (completed) setDialog({ task: { ...task, action: task.title }, mode: "done" });
      else setToast({ severity: "info", text: "Action reopened." });
    } catch (e) {
      setToast({ severity: "error", text: e?.response?.data?.message || "Could not update the action." });
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <AnalyticsPageShell
      title="SEO Origin & Backlinks"
      subtitle="On-site SEO foundation (Search Console, sitemap, technical SEO, core pages) and the backlink / link-building plan versus Atom, EPP and Eleven Plus Exams."
    >
      <Alert severity="info" sx={{ mb: 2 }} data-testid="seo-foundation-estimate-alert">
        Figures are research estimates and competitor notes (checked {formatYmd(LAST_CHECKED)}), not a live Search Console or Ahrefs feed. Re-check and update the data file when numbers change. Site:{" "}
        <Link href={OUR_SITE} target="_blank" rel="noopener noreferrer">{OUR_SITE}</Link>
      </Alert>

      <Paper sx={{ ...CARD_SX, p: 2, mb: 2 }}>
        <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
          <StatChip label="Origin topics" value={summary.originTopics} testId="seo-foundation-stat-origin" color="#2563eb" />
          <StatChip label="Backlink topics" value={summary.backlinkTopics} testId="seo-foundation-stat-backlinks" />
          <StatChip label="Missing" value={summary.missing} testId="seo-foundation-stat-missing" color="#b91c1c" />
          <StatChip label="Weak / partial" value={summary.weak} testId="seo-foundation-stat-weak" color="#b45309" />
          <StatChip label="Open actions" value={`${openCount}/${allActions.length}`} testId="seo-foundation-stat-open" />
          {progress.loading && <CircularProgress size={16} sx={{ alignSelf: "center" }} />}
        </Stack>
        {progress.error && <Alert severity="warning" sx={{ mt: 1 }}>{progress.error}</Alert>}
      </Paper>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 1.5 }} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="seo-foundation-pillar-label">Pillar</InputLabel>
          <Select labelId="seo-foundation-pillar-label" label="Pillar" value={filters.pillar} onChange={(e) => set({ pillar: e.target.value })} inputProps={{ "data-testid": "seo-foundation-pillar-select" }}>
            <MenuItem value="all">All pillars</MenuItem>
            {SECTIONS.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="seo-foundation-status-label">Foundation</InputLabel>
          <Select labelId="seo-foundation-status-label" label="Foundation" value={filters.foundation} onChange={(e) => set({ foundation: e.target.value })} inputProps={{ "data-testid": "seo-foundation-foundation-select" }}>
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="missing">Missing</MenuItem>
            <MenuItem value="weak">Weak / partial</MenuItem>
            <MenuItem value="strong">In place</MenuItem>
          </Select>
        </FormControl>
        <ToggleButtonGroup size="small" exclusive value={filters.status} onChange={(_, v) => v && set({ status: v })} aria-label="Action status filter">
          <ToggleButton value="all" sx={{ textTransform: "none", px: 1.5 }}>All</ToggleButton>
          <ToggleButton value="open" sx={{ textTransform: "none", px: 1.5 }}>Open</ToggleButton>
          <ToggleButton value="done" sx={{ textTransform: "none", px: 1.5 }}>Done</ToggleButton>
        </ToggleButtonGroup>
        <TextField size="small" label="Search" value={filters.search} onChange={(e) => set({ search: e.target.value })} inputProps={{ "data-testid": "seo-foundation-search" }} />
        <Typography variant="caption" color="text.secondary">Ticks and comments are shared with every admin.</Typography>
      </Stack>

      {!shown.length && <Typography variant="body2" color="text.secondary" data-testid="seo-foundation-empty">No topics match the filters.</Typography>}

      {shown.map((s) => {
        const all = allActions.filter((t) => t.pillarId === s.id);
        const done = all.filter((t) => progress.tasks[t.id]?.completed).length;
        return (
          <Accordion key={s.id} disableGutters sx={{ ...CARD_SX, mb: 1, "&:before": { display: "none" } }} data-testid={`seo-foundation-pillar-${s.id}`}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1, flex: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: s.color }} />
                <Typography sx={{ fontWeight: 700 }}>{s.label}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>{s.summary}</Typography>
                <Chip
                  size="small"
                  data-testid={`seo-foundation-progress-${s.id}`}
                  label={`${done}/${all.length} done`}
                  sx={{ fontWeight: 700, bgcolor: done === all.length ? "#ecfdf5" : "#f5f3ff", color: done === all.length ? DONE_GREEN : PURPLE }}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {s.topics.map((t) => {
                const st = STATUS[t.status] || STATUS.missing;
                return (
                  <Box key={t.id} data-testid={`seo-foundation-topic-${s.id}-${t.id}`} sx={{ border: "1px solid #eef0f4", borderRadius: 1.5, p: 1.5, mb: 1.25 }}>
                    <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontWeight: 700 }}>{t.name}</Typography>
                      <Chip size="small" label={st.label} data-testid={`seo-foundation-status-${s.id}-${t.id}`} sx={{ height: 20, fontSize: 11, fontWeight: 700, color: "#fff", bgcolor: st.color }} />
                    </Stack>
                    <Typography variant="body2"><strong>Leader:</strong> {t.leader.name} — {t.leader.note}</Typography>
                    <Typography variant="body2"><strong>Us:</strong> {t.us.note}{t.us.metric ? ` (${t.us.metric})` : ""}</Typography>
                    <Typography variant="body2" sx={{ mb: 0.75 }}><strong>Gap:</strong> {t.gap}</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PURPLE }}>Foundation actions</Typography>
                    {t.visibleActions.length ? t.visibleActions.map((act) => {
                      const task = { ...act, id: seoFoundationTaskId(s.id, t.id, act.id) };
                      return (
                        <ActionRow
                          key={task.id}
                          task={task}
                          state={progress.tasks[task.id]}
                          disabled={disabled}
                          busy={busyKey === task.id}
                          onToggle={onToggle}
                          onComments={(x) => setDialog({ task: { ...x, action: x.title }, mode: "comments" })}
                        />
                      );
                    }) : <Typography variant="caption" color="text.secondary">No actions match the status filter.</Typography>}
                  </Box>
                );
              })}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <TaskDialog
        task={dialog.task}
        mode={dialog.mode}
        comments={dialog.task ? progress.comments[dialog.task.id] || [] : []}
        onClose={() => setDialog({ task: null, mode: "comments" })}
        onSubmit={async (task, body, files) => { await progress.addComment(task.id, body, files); setToast({ severity: "success", text: "Comment added." }); }}
        onDelete={async (comment) => {
          try { await progress.deleteComment(comment); setToast({ severity: "success", text: "Comment deleted." }); } catch (e) { setToast({ severity: "error", text: e?.response?.data?.message || "Could not delete the comment." }); }
        }}
        onError={(text) => setToast({ severity: "error", text })}
      />
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? <Alert onClose={() => setToast(null)} severity={toast.severity} variant="filled" sx={{ width: "100%" }}>{toast.text}</Alert> : <span />}
      </Snackbar>
    </AnalyticsPageShell>
  );
}
